/* eslint-disable */
import { node } from "~/util/dom";
import { CONFIG } from "~/config";
import HistorySuggestItem from "~/es6/history.suggest.item";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { store } from "~/state/store";
import { getLocation } from "~/whereami";
import { isLocationUsable } from "~/util/validation";
import { addListener, makeEvent, removeListener } from "~/lib/signals";

interface ICallbacks {
	saveCb?: (type: string, data: any) => void,
	resetCb?: (type: string) => void,
	closeCb?: (type: string) => void,
}

export default class EditAddress {
	#callbacks?: ICallbacks;
	#container: HTMLDivElement;
	#buttons: {
		save: HTMLButtonElement,
		cancel: HTMLButtonElement,
	};
	#loader: HTMLImageElement;
	#input: HTMLInputElement;
	#errorNode: HTMLParagraphElement;
	#suggest: any;
	#suggestData: any;
	#data?: {
		type?: string,
		title?: string,
		id?: string,
	};
	#resultsCount: number;
	#suggestItems: Array<any>;
	#enterMode: boolean;
	#open: boolean;
	#cursor: any;
	#mapData: {
		title: string,
		source: string,
		id: string|number,
		longitude: number,
		latitude: number
	};

	#clickListener: string;

	constructor(callbacks: ICallbacks = {
		saveCb: () => {},
		resetCb: () => {},
		closeCb: () => {},
	}) {
		this.#container = node("div", { className: "address hide" });
		this.#loader = new Image();
		this.#loader.crossOrigin = "anonymous";
		this.#loader.src = "/img/loader.gif";
		this.#open = false;
		this.#enterMode = false;
		this.#suggestItems = [];
		this.#resultsCount = 0;
		this.#mapData = null;
		this.#callbacks = callbacks;
		this.#create();
		this.#setSuggest();

		if (CONFIG.wasmEnabled) {
			this.#input.addEventListener("focus", () => {
				makeEvent("inputmapclick-focus", this);
				this.#clickListener = addListener({}, "inputmapclick-click", data => {
					this.wasmMapClick(data);
				});
			});
			this.#input.addEventListener("blur", () => {
				makeEvent("inputmapclick-blur", this);
				this.#clickListener && removeListener(this.#clickListener);
				this.#clickListener = null;
			});
		}
	}

	destroy() {
		this.#clickListener && removeListener(this.#clickListener);
		this.#clickListener = null;
	}

	get container() {
		return this.#container;
	}

	get isOpen() {
		return this.#open;
	}

	open(data) {
		this.#data = data;

		this.#container.classList.remove("hide");
		this.#container.querySelector("h3").textContent = _("mymap.places.setAddress." +  this.#data.type.toLowerCase());
		this.#input.value = this.#data?.title || "";
		this.#input.focus();
		if (!CONFIG.wasmEnabled) {
			this.#cursor = Mapy.Cursor.add({ cursor: "crosshair", callback: this });
		}

		document.addEventListener("keydown", this);

		this.#open = true;
	}

	close() {
		this.#error("");
		this.#removeLoader();
		this.#container.classList.add("hide");
		this.#callbacks.closeCb(this.#data.type);
		this.#data = null;
		this.#input.value = "";
		this.#suggestData = null;
		this.#mapData = null;
		this.#enterMode = false;
		this.#suggestItems = [];
		this.#suggest.clearItems();
		if (!CONFIG.wasmEnabled) {
			Mapy.Cursor.remove(this.#cursor);
		}
		this.#cursor = null;

		document.removeEventListener("keydown", this);


		this.#open = false;
	}

	delete(data) {
		this.#data = data;
		this.#input.value = "";

		this.#save();
	}

	handleEvent(event: MouseEvent|KeyboardEvent) {
		if (event.type === "click") {
			if (event.target === this.#buttons.cancel) {
				this.close();
			}
			else if (event.target === this.#buttons.save) {
				this.#save();
			}
		}
		else if (event.type === "keydown") {
			let keyCode = event.which || event.keyCode;

			if (keyCode == 27) {
				this.close();
			}
		}
		else if (event.type === "input") {
			if (this.#container.classList.contains("error")) {
				this.#error();
			}
		}
	}

	/**
	 * Kliknuti do mapy pro nastaveni adresy.
	 *
	 * @param  {Object} e
	 */
	async mapClick(e) {
		const coords = await Scene.coordsFromEventAsync(e);

		this.#processCoords(coords);
	}

	wasmMapClick(data) {
		this.#processCoords(Scene.coordsFromWGS84(data.data.longitude, data.data.latitude));
	}

	#processCoords(coords) {
		this.#addLoader();

		// 18 -aby to dávalo konkrétní adresu
		getLocation(coords, 18).then(data => {
			// analytika - nove logovani sjednocene s appkou
			Mapy.Util.analyticsSearchSelection({
				query: "",
				result_type: "location_pick",
				search_type: "fixed_suggestion",
				search_purpose: this.#data.type,
				poi_title: data.label
			}, data);

			let wgs84 = coords.toWGS84();

			this.#mapData = {
				title: data.label,
				source: data.source,
				id: data.id,
				longitude: wgs84[0],
				latitude: wgs84[1]
			};
			this.#input.value = this.#mapData.title;

			// nastavene data -> ulozime
			this.#save();
		}, e => {
			this.#mapData = null;
			this.#removeLoader();
		});
	}

	/**
	 * Vytvoreni naseptavace a editace adresy.
	 */
	#create() {
		// editace adresy
		const addressTitle = node("h3", { textContent: "" });
		const desc = node("p", {}, _("mymap.places.edit.desc"))
		const suggestCover = node("div", { textContent: "", className: "suggest-cover" });
		const addressValue = node("input", { type: "text" });
		suggestCover.append(addressValue);

		const saveBtn = node("button", { type: "button", className: "save", textContent: _("save") });
		saveBtn.setAttribute("type", "button");
		saveBtn.setAttribute("data-log-ignore", "1");

		let cancelBtn = node("button", { type: "button", className: "cancel", textContent: _("cancel") });
		cancelBtn.setAttribute("type", "button");

		let error = node("p", { className: "error hide" });

		this.#container.append(addressTitle, desc, suggestCover, saveBtn, cancelBtn, error);

		saveBtn.addEventListener("click", this, false);
		cancelBtn.addEventListener("click", this, false);
		addressValue.addEventListener("input", this, false);

		this.#buttons = {
			save: saveBtn,
			cancel: cancelBtn,
		};
		this.#input = addressValue;
		this.#errorNode = error;
	}

	#setSuggest() {
		// naseptavac
		this.#suggest = new SMap.Suggest(this.#input, {
			factory: (data, pos) => {
				return new HistorySuggestItem(data, pos);
			},
			provider: new SMap.SuggestProvider({
				url: CONFIG.requestUrl + "/suggest/",
				poiaggUrl: CONFIG.requestUrl + "/poiagg",
				limit: 8,
			}),
			abTest: CONFIG.abTest ? CONFIG.abTest.flags : ""
		});
		this.#suggest.addListener("suggest", suggestData => {
			this.#suggestData = suggestData.data;

			this.#sendAnalytics(suggestData);

			this.#save();
		});
		this.#suggest.addListener("request-items", suggestItems => {
			this.#suggestItems = suggestItems;

			this.#resultsCount = suggestItems.length;

			if (this.#enterMode) {
				this.#enterMode = false;

				if (suggestItems.length) {
					this.#suggestData = this.#suggestItems[0];

					this.#sendAnalytics({
						position: 0,
						prevPhrase: this.#input.value.trim(),
						data: this.#suggestData
					});

					this.#save();
				}
				else {
					this.#removeLoader();
					this.#error(_("mymap.places.errorNoAddressFind"));
				}
			}
		});
		this.#suggest.addListener("request", suggestItems => {
			this.#resultsCount = 0;
		});
		this.#suggest.addListener("enter", () => {
			let value = this.#input.value.trim();

			this.#addLoader();

			if (value == this.#data.title) {
				this.close();
			}
			else if (!value && this.#data.title) {
				this.#save();
			}
			else if (this.#suggestItems.length) {
				this.#enterMode = false;
				this.#suggestData = this.#suggestItems[0];

				this.#sendAnalytics({
					position: 0,
					prevPhrase: this.#input.value.trim(),
					data: this.#suggestData
				});

				this.#save();
			}
			else if (value) {
				this.#error("");
				this.#enterMode = true;
				this.#suggestData = null;
				this.#suggest.send();
			}
		});
		this.#suggest.getProvider().updateParams(paramsObj => {
			const state = store.getState();
			const { user } = state.user;
			const { longitude, latitude, zoom } = state.map;

			paramsObj.lon = longitude;
			paramsObj.lat = latitude;
			paramsObj.zoom = Math.round(zoom);
			paramsObj.lang = CONFIG.langPriority.join(",");
			paramsObj.personalize = user.state === "green" ? 1 : 0;
			paramsObj.count = 8;

			if (isLocationUsable(state.user.location)) {
				paramsObj.userLat = state.user.location.latitude;
				paramsObj.userLon = state.user.location.longitude;
				paramsObj.userAccuracy = state.user.location.accuracy;
			}
		});
	}

	/**
	 * Zobrazeni chybove hlasky.
	 *
	 * @param  {String} [msg] Hlaska
	 */
	 #error(msg: string = "") {
		this.#errorNode.textContent = msg || "";
		this.#errorNode.classList[msg ? "remove" : "add"]("hide");

		this.#container.classList[msg ? "add" : "remove"]("error");
	}

	/**
	 * Ulozeni.
	 */
	#save() {
		// data - suggest nebo mapa
		let suggestMapData = this.#suggestData || this.#mapData;
		let data = this.#data;
		let value = this.#input.value;
		let logger = Mapy.getComponent("logger");

		this.#addLoader();

		if (value.length && suggestMapData) {
			let like = {
				type: this.#data.type,
				title: suggestMapData.title,
				mark: {
					lon: suggestMapData.longitude,
					lat: suggestMapData.latitude
				},
				...suggestMapData.source === "coor" ? {
					dataType: "point",
				}
				:{
					dataType: "entity",
					source: suggestMapData.source,
					source_id: suggestMapData.id,
				}
			};

			let update = data && data.id;
			let prom = null;

			if (update) {
				prom = Mapy.ProviderMapyBox.likeUpdate(data.id, like, {
					"1.mark.lon": "float",
					"1.mark.lat": "float"
				});
			}
			else {
				prom = Mapy.ProviderMapyBox.likeCreate(like, {
					"0.mark.lon": "float",
					"0.mark.lat": "float"
				});
			}

			logger.log(`${this.#data.type}_${update ? "edit" : "set"}`);

			prom.then(createData => {
				this.#error("");

				// update dat
				if (!data) {
					this.#data[this.#data.type] = {};
					data = this.#data[this.#data.type];
				}

				let newData = {
					id: update ? data.id : createData.id,
					...like.dataType === "entity" ? {
						source: like.source,
						source_id: like.source_id,
					}
					: {},
					dataType: like.dataType,
					title: like.title,
					mark: like.mark,
				};

				this.#suggestData = null;
				this.#callbacks.saveCb(this.#data.type, newData);
			}, e => {
				// eslint-disable-next-line no-console
				console.error(e);
				this.#error(_("mymap.places.errorSave"));
			});
		}
		else if (data && data.id && !value.length) {
			logger.log(`${this.#data.type}_reset`);
			Mapy.ProviderMapyBox.likeDelete(data.id, {
				type: data.type,
			}).then(() => {
				this.#error("");

				delete data.id;
				data.title = "";

				this.#suggestData = null;
				this.#callbacks.resetCb(this.#data.type);
			}, e => {
				// eslint-disable-next-line no-console
				console.error(e);
				this.#error(_("mymap.places.errorDelete"));
				this.#removeLoader();
			});
		}
		else {
			if (value !== this.#data.title) {
				this.#error(_("mymap.places.errorNoAddressFind"));
				this.#removeLoader();
			}
			else {
				this.close();
			}
		}
	}

	#addLoader() {
		this.#container.append(this.#loader);

		this.#buttons.cancel.remove();
		this.#buttons.save.remove();
	}

	#removeLoader() {
		this.#loader.remove();

		this.#errorNode.before(this.#buttons.save);
		this.#errorNode.before(this.#buttons.cancel);
	}

	// nove logovani sjednocene s appkou
	#sendAnalytics(data) {
		// analytika
		Mapy.Util.analyticsSearchSelection({
			query: data.prevPhrase.trim().toLowerCase(),
			search_type: "suggestion",
			index: data.position + 1,
			search_purpose: this.#data.type,
			name: data.data.title.trim()
		}, data.data, data.data);
	}
}
